import type { ReactElement } from "react";
import { TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { DailyRanksKeyword } from "@/types/rankTracker";
import { AD_PUSH_META, classifyAdPush, type AdPushCutoffs } from "./adPush";

export interface AdPushCellProps {
  /** The keyword this row is for -- its two server-computed 7-day facts drive the classifier. */
  keyword: DailyRanksKeyword;
  /** The brand's cutoffs (WEB-002's triple). `index.tsx` passes the committed cutoffs from
   * `useGetAdPushCutoffs`, falling back to `DEFAULT_CUTOFFS` before they load; WEB-004 extends this
   * to pass the draft while the rules panel is open (live relabel). */
  cutoffs: AdPushCutoffs;
  /** Both view only: the "Org"/"Spn" sub-row labels that annotate the stacked day cells. They moved
   * here from the slice-A `Rel.` cell when the Ad push column became the last column left of the day
   * columns (design capture § Composition consequence). */
  showRowLabels?: boolean;
}

/**
 * The Ad push cell on a keyword row: the `AD_PUSH_META` pill (icon + name + colours) for the label
 * `classifyAdPush` returns over this keyword's `adPushAvgOrganicRank` / `adPushAdRunning` and the
 * brand's cutoffs. Hover shows the reason (bold) then the meaning. The pill's own click is
 * stopped so it never also opens the row's keyword-detail modal.
 *
 * Product and theme rows do NOT render this component -- they render a bare `<TableCell />` (empty,
 * not `–`): a suggestion averaged over a set of keywords does not exist for a roll-up row
 * (Decision D-4).
 */
export function AdPushCell({ keyword, cutoffs, showRowLabels = false }: AdPushCellProps): ReactElement {
  const result = classifyAdPush(keyword.adPushAvgOrganicRank, keyword.adPushAdRunning, cutoffs);
  const meta = AD_PUSH_META[result.label];
  const Icon = meta.icon;

  return (
    <TableCell className="pr-1">
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="secondary"
              className={cn("shrink-0 cursor-default gap-1 whitespace-nowrap", meta.badgeClass)}
              onClick={(e) => e.stopPropagation()}
            >
              <Icon className="h-3 w-3 shrink-0" />
              {meta.name}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-[280px] space-y-1">
            <p className="font-semibold">{result.reason}</p>
            <p>{meta.meaning}</p>
          </TooltipContent>
        </Tooltip>
        {showRowLabels && (
          <div className="ml-auto flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
            <span className="leading-8">Org</span>
            <span className="leading-4">Spn</span>
          </div>
        )}
      </div>
    </TableCell>
  );
}
